import React from 'react';
import { Link } from 'react-router-dom';
import {
  Trophy,
  Activity,
  Brain,
  GraduationCap,
  HandshakeIcon,
  BarChart3,
  ArrowRight,
  ShieldCheck,
  Zap,
  HeartPulse,
  Target,
  Users,
  TrendingUp,
} from 'lucide-react';
import { SectionHeading, FeatureCard, KpiTile, ImageBlock } from '@/components/Pieces';
import ContactForm from '@/components/ContactForm';
import FAQ from '@/components/FAQ';
import WorkforceROICalculator from '@/components/WorkforceROICalculator';
import HomeVideoSection from '@/components/HomeVideoSection';
import usePageMeta from '@/hooks/usePageMeta';
import { img } from '@/lib/site';

const DIVISIONS = [
  {
    icon: Trophy,
    title: 'Corporate Games & Leagues',
    desc: 'Corporate sports leagues and team challenges that improve employee engagement, morale, retention, and cross-department collaboration.',
    href: '/corporate-leagues',
    img: img('divisions_corporate'),
  },
  {
    icon: Activity,
    title: 'Workforce Performance',
    desc: 'Recovery, mobility, and biometric readiness systems that reduce burnout, improve employee energy, and strengthen retention across industrial and corporate teams.',
    href: '/workforce-systems',
    img: img('divisions_workforce'),
  },
  {
    icon: GraduationCap,
    title: 'Next Gen Academy',
    desc: 'A youth development, leadership, and family support ecosystem that helps employers support working families and strengthen long-term retention.',
    href: '/next-gen-academy',
    img: img('divisions_academy'),
  },
];

const PILLARS = [
  { icon: Zap, title: 'Speed & Power', desc: 'Acceleration mechanics, vertical development, position-specific drills.' },
  { icon: HeartPulse, title: 'Recovery Systems', desc: 'Mobility, hydration, sleep, biometric readiness, shift recovery.' },
  { icon: Brain, title: 'Mental Resilience', desc: 'Performance psychology, pressure management, leadership focus.' },
  { icon: ShieldCheck, title: 'Injury Reduction', desc: 'Workplace durability, return-to-play, mobility programming.' },
  { icon: Target, title: 'Measurable Outcomes', desc: 'KPIs across engagement, readiness, retention, fatigue.' },
  { icon: Users, title: 'Culture & Leadership', desc: 'Team communication, accountability systems, emotional discipline.' },
];

export default function HomePage() {
  usePageMeta({
    title: 'Ball Raw Performance | Employee Engagement, Workforce Performance & Corporate Retreat Partner',
    description:
      'Ball Raw Performance is a workforce performance, employee engagement, and corporate experience partner. We help organizations improve retention, culture, wellbeing, and team performance through corporate sports leagues, wellness experiences, retreat partnerships, and family development programs.',
    path: '/',
  });
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative bg-[#0A0A0A] text-white overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={img('hero_home')}
            alt="Elite athlete sprinting"
            className="w-full h-full object-cover opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/30" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-40 lg:py-48">
          <p className="brp-overline brp-reveal">Workforce + Performance Systems</p>
          <h1 className="brp-reveal brp-delay-1 font-display uppercase font-bold tracking-[-0.01em] text-5xl md:text-7xl lg:text-8xl leading-[0.92] mt-6 max-w-5xl">
            Workforce Performance
            <br />
            Built Like <span className="text-[#D4AF37]">Elite Sports.</span>
          </h1>
          <p className="brp-reveal brp-delay-2 mt-8 text-lg md:text-xl text-gray-300 max-w-2xl leading-relaxed">
            Ball Raw Performance helps organizations improve employee engagement, retention, culture, and workforce performance through corporate sports leagues, wellness experiences, recovery and readiness systems, and family development programs.
          </p>
          <div className="brp-reveal brp-delay-3 mt-10 flex flex-wrap gap-4">
            <Link to="/partnerships#contact" className="btn-brp-primary" data-testid="hero-cta-discovery">
              Schedule Discovery Call <ArrowRight size={16} />
            </Link>
            <Link to="/workforce-systems" className="btn-brp-outline-light" data-testid="hero-cta-explore">
              Explore Programs
            </Link>
          </div>
          <div className="brp-reveal brp-delay-4 mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 max-w-3xl">
            {[
              { v: '+42%', l: 'Engagement Lift' },
              { v: '78%', l: 'Participation Rate' },
              { v: '-31%', l: 'Fatigue Reduction' },
              { v: '+18%', l: 'Retention (12mo)' },
            ].map((s) => (
              <div key={s.l} className="bg-[#0A0A0A] p-5">
                <div className="font-display text-3xl md:text-4xl font-bold text-[#D4AF37]">
                  {s.v}
                </div>
                <div className="mt-1 text-[10px] uppercase tracking-[0.2em] text-gray-400 font-bold">
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#D4AF37]" />
      </section>

      {/* CREDIBILITY */}
      <section className="bg-white py-14 md:py-16 px-6 md:px-10" data-testid="credibility-section">
        <div className="max-w-[1400px] mx-auto text-center">
          <h2 className="font-display uppercase tracking-[0.25em] text-sm md:text-base font-bold text-[#0A0A0A]">
            Organizations We&rsquo;ve Worked With
          </h2>
          <div className="mt-10 flex items-center justify-center gap-12 md:gap-20 flex-wrap">
            <img
              src="/logos/ohio-state.svg"
              alt="The Ohio State University"
              className="h-8 md:h-10 w-auto"
              data-testid="credibility-logo-ohio-state"
            />
            <img
              src="/logos/ufc.svg"
              alt="UFC"
              className="h-8 md:h-11 w-auto"
              data-testid="credibility-logo-ufc"
            />
          </div>
        </div>
      </section>

      {/* VIDEO OVERVIEW */}
      <HomeVideoSection />

      {/* DIVISIONS */}
      <section className="py-20 md:py-32 px-6 md:px-10 bg-white">
        <div className="max-w-[1400px] mx-auto">
          <SectionHeading
            overline="Three Core Divisions"
            title="One Integrated Performance Ecosystem."
            subtitle="Ball Raw Performance is a workforce performance, employee engagement, and corporate experience partner. We unify culture, recovery, and family development into one measurable system — built for HR, Operations, and Executive leaders."
          />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {DIVISIONS.map((d) => (
              <Link
                key={d.title}
                to={d.href}
                className="brp-card p-0 group block"
                data-testid={`division-card-${d.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <ImageBlock src={d.img} alt={d.title} ratio="aspect-[4/3]" />
                <div className="p-8">
                  <div className="w-12 h-12 flex items-center justify-center border border-[#D4AF37] mb-4">
                    <d.icon size={22} strokeWidth={1.75} className="text-[#D4AF37]" />
                  </div>
                  <h3 className="font-display uppercase tracking-wide text-2xl font-bold">
                    {d.title}
                  </h3>
                  <p className="mt-4 text-[#555] leading-relaxed">{d.desc}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-[#0A0A0A] font-display uppercase text-xs tracking-[0.2em] group-hover:text-[#D4AF37] transition">
                    Explore Division <ArrowRight size={14} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* MEASURABLE RESULTS */}
      <section className="py-20 md:py-32 px-6 md:px-10 bg-[#0A0A0A] text-white brp-noise relative">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 relative">
          <div className="lg:col-span-5">
            <SectionHeading
              light
              overline="Measurable Outcomes"
              title="Performance, Quantified."
              subtitle="Every engagement ships with the KPIs HR, Operations, and Executive leaders already track — engagement, retention, morale, readiness, and culture — measured quarter over quarter."
            />
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-px bg-[#D4AF37]/30">
            <KpiTile value="+42" suffix="%" label="Engagement Lift" testId="kpi-engagement" />
            <KpiTile value="78" suffix="%" label="League Participation" testId="kpi-participation" />
            <KpiTile value="-31" suffix="%" label="Fatigue Reduction" testId="kpi-fatigue" />
            <KpiTile value="+18" suffix="%" label="Retention 12mo" testId="kpi-retention" />
            <KpiTile value="84" suffix="" label="Avg Readiness Score" testId="kpi-readiness" />
            <KpiTile value="92" suffix="%" label="Recovery Compliance" testId="kpi-recovery" />
          </div>
        </div>
      </section>

      {/* ROI CALCULATOR */}
      <section className="py-20 md:py-32 px-6 md:px-10 bg-white" id="roi-calculator">
        <div className="max-w-[1400px] mx-auto">
          <WorkforceROICalculator
            title="Free Workforce Performance ROI Calculator"
            subtitle={"What is workforce performance costing your organization? Estimate turnover exposure, lost workday costs, potential financial opportunity, ROI, and payback using your organization\u2019s actual workforce data."}
            sourcePage="home"
            testIdPrefix="home-roi"
          />
        </div>
      </section>

      {/* PILLARS GRID */}
      <section className="py-20 md:py-32 px-6 md:px-10 bg-white">
        <div className="max-w-[1400px] mx-auto">
          <SectionHeading
            overline="Performance Pillars"
            title="Six Systems. One Standard."
            subtitle="Sports science doesn't belong in a silo. We translate every pillar into workforce outcomes leadership teams can measure."
          />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PILLARS.map((p, i) => (
              <FeatureCard
                key={p.title}
                icon={p.icon}
                title={p.title}
                desc={p.desc}
                accent
                testId={`pillar-${i}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CORPORATE LEAGUES TEASER */}
      <section className="py-20 md:py-32 px-6 md:px-10 bg-[#F9F9F9]">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <ImageBlock src={img('leagues_basketball')} alt="Corporate basketball league" ratio="aspect-[4/3]" caption="Corporate Leagues" />
          <div>
            <SectionHeading
              overline="Corporate Games & Competitive Leagues"
              title="Engagement, Played Like a Season."
              subtitle="Corporate sports leagues and team competitions designed to improve employee engagement, morale, workplace culture, and cross-department collaboration — investments in connection, delivered as a season."
            />
            <ul className="mt-8 space-y-3">
              {[
                'Interdepartment seasonal leagues',
                'Branded jerseys, schedules, and championships',
                'Family participation events',
                'Leadership-through-competition programming',
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-[#0A0A0A]">
                  <span className="mt-1 w-2 h-2 bg-[#D4AF37] inline-block flex-shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <Link to="/corporate-leagues" className="btn-brp-outline-dark mt-8" data-testid="cta-leagues">
              Explore Corporate Leagues
            </Link>
          </div>
        </div>
      </section>

      {/* RECOVERY + RESILIENCE BENTO */}
      <section className="py-20 md:py-32 px-6 md:px-10 bg-white">
        <div className="max-w-[1400px] mx-auto">
          <SectionHeading
            overline="Recovery, Readiness & Mental Resilience"
            title="The Edge Lives Between Sessions."
            subtitle="Recovery, readiness, and resilience are workforce infrastructure — not perks. Deployed at scale to reduce burnout, strengthen retention, and protect employee wellbeing."
          />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-6">
            <div className="md:col-span-7">
              <ImageBlock src={img('recovery_systems')} alt="Recovery systems" ratio="aspect-[16/10]" caption="Recovery Systems" />
            </div>
            <div className="md:col-span-5 grid grid-cols-1 gap-6">
              <FeatureCard
                icon={HeartPulse}
                title="Biometric Readiness"
                desc="Wearable integration, HRV monitoring, fatigue & sleep trends, shift-recovery scoring."
              />
              <FeatureCard
                icon={Brain}
                title="Mental Resilience"
                desc="Pressure management, focus development, leadership under load — framed as performance psychology."
              />
            </div>
            <div className="md:col-span-5">
              <FeatureCard
                icon={ShieldCheck}
                title="Injury Reduction"
                desc="Mobility programming, workplace durability protocols, and return-to-play frameworks adapted for industrial teams."
              />
            </div>
            <div className="md:col-span-7">
              <ImageBlock src={img('mental_resilience')} alt="Mental resilience training" ratio="aspect-[16/10]" caption="Performance Psychology" />
            </div>
          </div>
        </div>
      </section>

      {/* YOUTH ACADEMY TEASER */}
      <section className="py-20 md:py-32 px-6 md:px-10 bg-[#0A0A0A] text-white relative">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionHeading
              light
              overline="Ball Raw Next Gen Academy"
              title="Stronger Athletes. Stronger Families."
              subtitle="Youth development, leadership, and mentorship paired with family support — homework blocks, transportation, athlete report cards, and confidence-building. Employers use the Academy to support working families and strengthen long-term retention and community impact."
            />
            <Link to="/next-gen-academy" className="btn-brp-primary mt-8" data-testid="cta-academy">
              Explore the Academy
            </Link>
          </div>
          <ImageBlock src={img('youth_academy_hero')} alt="Youth academy" ratio="aspect-[4/3]" caption="Youth Development" />
        </div>
      </section>

      {/* PARTNERSHIP CTA */}
      <section className="py-20 md:py-32 px-6 md:px-10 bg-[#F9F9F9]">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <SectionHeading
              overline="Partnership Opportunities"
              title="A Partner Built To Plug Into Your Workforce, Retreat, Or Community Ecosystem."
              subtitle="Ball Raw Performance partners with organizations, retreat companies, event companies, schools, sports organizations, and wellness providers — integrating into existing HR, PEO, and operations ecosystems without adding administrative load."
            />
            <div className="mt-8 grid grid-cols-2 gap-3">
              {['Corporate', 'HR & PEO', 'Schools', 'Sports Orgs', 'Hospitality', 'Healthcare'].map((t) => (
                <div key={t} className="border border-[#0A0A0A] py-3 px-4 font-display uppercase text-xs tracking-[0.18em] text-[#0A0A0A]">
                  {t}
                </div>
              ))}
            </div>
            <Link to="/partnerships" className="btn-brp-primary mt-10" data-testid="cta-partnership">
              Become a Partner <ArrowRight size={16} />
            </Link>
          </div>
          <div className="lg:col-span-7">
            <ContactForm
              formType="discovery_call"
              variant="dark"
              title="Schedule Your Discovery Call"
              subtitle="Talk to a partner specialist about your workforce, athletes, or partnership idea."
              showRole
              showEmployees
              showIndustry
              showInterest
              ctaLabel="Request Discovery Call"
              testIdPrefix="home-contact"
              sourcePage="/"
            />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-32 px-6 md:px-10 bg-white">
        <div className="max-w-[1400px] mx-auto">
          <FAQ
            testIdPrefix="home-faq"
            overline="Frequently Asked"
            title="Frequently Asked Questions"
            subtitle="Answers to common questions from HR leaders, Operations managers, employers, PEOs, strategic partners, schools, and event planners."
            items={[
              {
                q: 'What does Ball Raw Performance actually do?',
                a: 'Ball Raw Performance helps organizations improve workforce performance through employee engagement, team building, leadership experiences, and family focused programs. Our services are designed to strengthen workplace culture, improve retention, and support long term business success.',
              },
              {
                q: 'Who is Ball Raw Performance designed to help?',
                a: 'We partner with employers, HR teams, Operations leaders, PEOs, municipalities, schools, and organizations that want to invest in their people. Every program is customized to support your workforce, business goals, and organizational culture.',
              },
              {
                q: 'How is Ball Raw Performance different from a traditional consulting firm?',
                a: 'We do more than provide recommendations. We work alongside your organization to implement workforce performance programs that employees experience and leaders can measure.',
              },
              {
                q: 'How can Ball Raw Performance help organizations improve business performance?',
                a: 'Our programs help organizations strengthen employee engagement, collaboration, leadership, and workplace culture. Investing in your people supports long term organizational performance and business growth.',
              },
              {
                q: 'What business outcomes do organizations typically hope to achieve?',
                a: 'Organizations partner with Ball Raw Performance to strengthen employee engagement, improve retention, develop leaders, and build stronger workplace relationships. Every program is aligned with the unique goals of the organization.',
              },
              {
                q: 'How do we get started?',
                a: 'We begin with a discovery conversation to understand your workforce, business objectives, and current challenges. From there, we recommend the services that best fit your organization.',
              },
            ]}
          />
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[#0A0A0A] text-white py-20 md:py-28 px-6 md:px-10 border-t border-[#D4AF37]/30">
        <div className="max-w-[1400px] mx-auto text-center">
          <p className="brp-overline">Final Word</p>
          <h2 className="font-display uppercase tracking-tight font-bold text-5xl md:text-7xl mt-6">
            Claim Your <span className="text-[#D4AF37]">Game.</span>
          </h2>
          <p className="mt-8 text-gray-400 max-w-2xl mx-auto">
            Build a workforce performance plan with the partner trusted by HR, Operations, and Executive leaders to improve employee engagement, retention, culture, and wellbeing.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link to="/partnerships#contact" className="btn-brp-primary" data-testid="final-cta-build">
              Build a Workforce Performance Plan
            </Link>
            <Link to="/partnerships" className="btn-brp-outline-light" data-testid="final-cta-partner">
              Become a Partner
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
